import { get, post } from './transport.js';
import { memoryRevocationResponseSchema, subjectMemoryResponseSchema, } from './intelligencePersonalization.js';
/**
 * A 2xx whose body this client could not read.
 *
 * DISTINCT FROM A TRANSPORT FAILURE ON PURPOSE. Every failure branch in both
 * apps ends in "try again" because that is the honest advice for a dropped
 * connection and a 500. It is not honest here: the server answered, and it will
 * answer identically the next hundred times. The remedy is a release, not a
 * retry, and a person owed a truthful account of what is held about them should
 * not be left pressing a button that cannot work.
 *
 * It carries NO body and NO parsed fragment. A response we do not understand is
 * the last thing that should be forwarded into a log or a screen.
 */
export class MemoryContractError extends Error {
    issueCount;
    memoryContract = true;
    constructor(issueCount) {
        super('The memory response did not match the contract this client understands.');
        this.issueCount = issueCount;
        this.name = 'MemoryContractError';
    }
}
/** Structural, not `instanceof` — three `ApiError` classes already taught us. */
export function isMemoryContractError(err) {
    return (typeof err === 'object' &&
        err !== null &&
        err.memoryContract === true);
}
function parseOrThrow(schema, body) {
    const parsed = schema.safeParse(body);
    if (parsed.success)
        return parsed.data;
    throw new MemoryContractError(parsed.error.issues.length);
}
/**
 * `GET /api/intelligence/memory/about-me` — every memory item whose subject is
 * the caller, revoked and expired ones included.
 *
 * `limit` shortens the page; it cannot widen it past the server's own 500, and
 * it names no subject. A short page sets `windowExhausted`, which is the
 * difference between "this is all of it" and "this is as much as we returned".
 */
export async function aboutMe(options) {
    const { limit, ...rest } = options ?? {};
    // THE PATH IS A LITERAL, INLINE, AND THAT IS NOT AN ACCIDENT OF STYLE.
    // `capability-parity-census.test.ts` builds its route -> caller map by
    // reading `get('/...')` and `post('/...')` out of THIS package's source; a
    // path held in a `const` above is invisible to it, so the ledger would keep
    // reporting a backend-only gap over a screen that exists. A caller the
    // parity census cannot see is a caller the ledger will forget.
    const body = await get('/api/intelligence/memory/about-me', {
        ...rest,
        ...(limit === undefined ? {} : { query: { limit } }),
    });
    return parseOrThrow(subjectMemoryResponseSchema, body);
}
/** `source` of a preference Phibian learned from what the person said (preferenceLearning.ts). */
export const LEARNED_PREFERENCE_SOURCE = 'ask_phibian_explicit_statement';
/**
 * The closed choice list, mirrored from the API's PRESENTATION_PREFERENCE_CHOICES
 * (the server refuses anything else with a 400).
 */
export const PREFERENCE_CHOICES = {
    answer_length: { label: 'Answer length', values: { concise: 'Short', detailed: 'Detailed' } },
    answer_format: { label: 'Answer format', values: { table: 'Table', bullets: 'Bullet points' } },
    answer_breakdown: {
        label: 'Break figures down',
        values: { by_program: 'By program', by_site: 'By site', by_role: 'By role' },
    },
};
/**
 * Change one learned presentation preference about the caller (closed
 * vocabulary: e.g. answer_length = concise | detailed). The previous value of
 * that kind is superseded; turning one off is `revoke`.
 */
export async function setPreference(kind, value, options) {
    return post('/api/intelligence/memory/about-me/preferences', { kind, value }, options);
}
/**
 * `POST /api/intelligence/memory/about-me/revocations` — retire one item.
 *
 * ONE ARGUMENT, and it is not a subject. There is deliberately no `reason`
 * either: the server fixes it to the closed-vocabulary token `user_request`, so
 * nothing a person types can reach an audit row.
 *
 * 404 means "no such memory about you" and covers both "no such item" and "not
 * yours" — the route is not an existence oracle and a client must not present
 * it as one. 409 means it was already revoked.
 */
export async function revoke(memoryId, options) {
    const body = await post('/api/intelligence/memory/about-me/revocations', { memoryId }, options);
    return parseOrThrow(memoryRevocationResponseSchema, body);
}
function httpStatusOf(err) {
    if (typeof err !== 'object' || err === null)
        return null;
    const status = err.status;
    return typeof status === 'number' && Number.isFinite(status) ? status : null;
}
function permissionNamedBy(err) {
    if (typeof err !== 'object' || err === null)
        return null;
    const direct = err.permission;
    if (typeof direct === 'string' && direct.trim() !== '')
        return direct.trim();
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return null;
    const named = body.permission;
    return typeof named === 'string' && named.trim() !== '' ? named.trim() : null;
}
/**
 * Why a read failed, at the resolution the reader can act on.
 *
 * The contract check is FIRST and deliberately so: a `MemoryContractError`
 * carries no `status`, so every ordering that tests `status === null` before it
 * classes a parse failure as "you have no connection" — the exact mistake the
 * worker app's `failureKind` docblock records having shipped once already.
 */
export function memoryReadFailure(err) {
    if (isMemoryContractError(err))
        return { state: 'misunderstood' };
    const status = httpStatusOf(err);
    if (status === 401)
        return { state: 'signed-out' };
    if (status === 403)
        return { state: 'refused', permission: permissionNamedBy(err) };
    return { state: 'unreadable' };
}
/** The sentence for a failed read. Never says anything about what is held. */
export function readFailureSentence(failure) {
    switch (failure.state) {
        case 'signed-out':
            return 'Your sign-in has ended, so this could not be loaded. Nothing about what Phibian records has changed — sign in again to see it.';
        case 'refused':
            return failure.permission === null
                ? 'Phibian declined to answer this for your account. That is unexpected: seeing your own record is not a permission anybody grants, so this is worth reporting rather than retrying.'
                : `Phibian declined to answer this for your account, naming the ${failure.permission} permission. That is unexpected: seeing your own record is not a permission anybody grants, so this is worth reporting rather than retrying.`;
        case 'misunderstood':
            return 'Phibian answered, but this app could not read the answer. Do NOT read this as an empty record — something may well be held. Trying again will produce the same result; this needs reporting.';
        case 'unreadable':
            return 'Phibian could not be reached just now, so this could not be loaded. Do not read this as an empty record — nothing has been shown to you either way.';
    }
}
export function memoryOrigin(record) {
    const evidence = record.evidenceClass;
    switch (evidence) {
        case 'EXPLICIT_PREFERENCE':
            return record.explicitFlag ? 'stated' : 'unclear';
        case 'INFERRED':
            return record.explicitFlag ? 'unclear' : 'inferred';
        case 'AUTHORITATIVE':
            return record.explicitFlag ? 'declared' : 'unclear';
        case 'OBSERVED_PATTERN':
            return record.explicitFlag ? 'unclear' : 'observed';
        case 'OUTCOME':
            // The domain constrains `explicitFlag` for none of the outcome cases, so
            // it carries no claim either way and there is nothing to contradict.
            return 'outcome';
    }
}
/** Origin in the reader's own words. Never the enum, never the class name. */
export function originSentence(origin) {
    switch (origin) {
        case 'stated':
            return 'You told Phibian this.';
        case 'observed':
            return 'Phibian recorded this from what happened in your work. You did not state it.';
        case 'inferred':
            return 'Phibian worked this out from other things. Nobody stated it and nothing recorded it directly.';
        case 'declared':
            return 'This came from your organization’s records or its policy, not from you.';
        case 'outcome':
            return 'This is the recorded result of something that was decided or done.';
        case 'unclear':
            return 'This record’s own provenance fields disagree about where it came from, so Phibian cannot tell you. That is worth reporting.';
    }
}
export function memoryUseState(record, nowMs) {
    if (record.intelligenceUsesThis)
        return 'in-use';
    if (record.revocationState === 'revoked')
        return 'revoked';
    if (record.revocationState === 'superseded')
        return 'superseded';
    const to = record.effectiveTo === null ? null : Date.parse(record.effectiveTo);
    if (to !== null && Number.isFinite(to) && to < nowMs)
        return 'expired';
    const from = Date.parse(record.effectiveFrom);
    if (Number.isFinite(from) && from > nowMs)
        return 'not-yet-in-effect';
    return 'not-in-use-unexplained';
}
/**
 * RETAINED AND IN USE ARE DIFFERENT FACTS, and this is where they are kept
 * apart in words. Every branch below except the first is a thing Phibian STILL
 * HOLDS — none of them is a deletion, and none of them may be worded as one.
 */
export function useSentence(state) {
    switch (state) {
        case 'in-use':
            return 'In use: Phibian can draw on this when it answers you.';
        case 'revoked':
            return 'You revoked this. Phibian still holds the record that it existed and that you revoked it, and it no longer reaches Phibian’s answers.';
        case 'superseded':
            return 'Replaced by a newer record. This one is still held and no longer reaches Phibian’s answers.';
        case 'expired':
            return 'Past its retention window. Still held, and no longer reaching Phibian’s answers.';
        case 'not-yet-in-effect':
            return 'Not in effect yet. Held, and not reaching Phibian’s answers today.';
        case 'not-in-use-unexplained':
            return 'Phibian says this is not reaching its answers, and the record does not say why. It is still held. That is worth reporting.';
    }
}
export function memoryHolding(response, nowMs) {
    if (response.records.length === 0) {
        // `windowExhausted` cannot be true here — it is `rows.length >= limit` and
        // the limit is at least 1 — so an empty page is genuinely an empty record.
        return { kind: 'absent' };
    }
    const states = response.records.map((record) => memoryUseState(record, nowMs));
    const count = (state) => states.filter((s) => s === state).length;
    return {
        kind: 'held',
        held: response.records.length,
        inUse: count('in-use'),
        revoked: count('revoked') + count('superseded'),
        expired: count('expired'),
        notYet: count('not-yet-in-effect'),
        unexplained: count('not-in-use-unexplained'),
        countIsFloor: response.windowExhausted,
    };
}
/**
 * The headline sentence. ONE function, so the four states cannot be worded
 * into each other by a screen that only remembered three of them.
 */
export function holdingSentence(holding) {
    if (holding.kind === 'absent') {
        return 'Phibian records nothing about you. Nothing has been withheld from this page — there is nothing in it.';
    }
    const total = holding.countIsFloor
        ? `At least ${holding.held} records are held about you (this page is full, so there may be more).`
        : `${holding.held} ${holding.held === 1 ? 'record is' : 'records are'} held about you.`;
    if (holding.inUse === 0) {
        return `${total} None of them is currently reaching Phibian’s answers. That is not the same as nothing being recorded: everything below is still held.`;
    }
    return `${total} ${holding.inUse} ${holding.inUse === 1 ? 'is' : 'are'} currently reaching Phibian’s answers; the rest are held and not in use.`;
}
/**
 * THE SCOPE OF THIS PAGE, said out loud on both surfaces.
 *
 * The route lists rows whose subject is this person. Organization- and
 * role-subject memory is hydrated into every turn as well and is not here,
 * because it is not about them. Leaving that unsaid would let the page imply
 * "this is everything that shapes what Phibian says to you", which is false.
 */
export const MEMORY_SCOPE_NOTE = 'This is what Phibian records about YOU. Your organization also has memory of its own — policies, and context attached to roles rather than to people — which shapes Phibian’s answers and is not listed here because it is not about you.';
/**
 * WHY THERE IS NO EDIT BUTTON. The server sends this reason in the response
 * (`correction.detail`) precisely so a screen can answer the question rather
 * than leaving a person to guess, and a screen SHOULD render the server's own
 * words. This is the framing around them, and it is here so both apps use one.
 */
export const MEMORY_CORRECTION_HEADING = 'Why you can remove a record but not edit it';
/**
 * WHAT "FORGET" ACTUALLY DOES, said before the button is pressed and not after.
 *
 * A revocation is an APPENDED ROW. `intelligence_memory_item` and
 * `intelligence_memory_revocation` are both APPEND_ONLY — `cwos_app` holds no
 * UPDATE and no DELETE on either — so nothing this button can reach erases the
 * item. What it does is real and it is worth doing: `hydrateForTurn` excludes a
 * revoked row in SQL, so the item is not loaded into the next turn at all and
 * therefore cannot reach a model.
 *
 * THE PHYSICAL DELETE IS NOT WIRED, AND THIS SENTENCE EXISTS BECAUSE OF IT.
 * `MemoryStore.purgeRevokedPersonAdjacent` and `purgeUserOnOffboarding` do
 * delete rows, and neither has a caller anywhere in `apps/api/src` — measured,
 * not assumed. So "forgotten" here means "no longer reaching Phibian's answers",
 * never "gone", and a screen that let a person infer the second from the first
 * would have made this whole surface a more comfortable lie than no surface at
 * all.
 */
export const MEMORY_REVOKE_MEANS = 'Removing a record stops Phibian drawing on it — it is left out of every answer from your next question onwards. It does not erase it: your organization keeps the record, and the fact that you removed it, as part of its own history. Phibian cannot rewrite either.';
export function memoryRecordView(record, nowMs) {
    const origin = memoryOrigin(record);
    const use = memoryUseState(record, nowMs);
    return {
        record,
        origin,
        originSentence: originSentence(origin),
        use,
        useSentence: useSentence(use),
        revocable: use === 'in-use' || use === 'not-yet-in-effect',
        hasUnresolvedEvidence: record.provenanceRefs.length > 0 || (record.payload.refs ?? []).length > 0,
    };
}
//# sourceMappingURL=intelligenceMemory.js.map